import { PaperClip } from "@/components/bnp-icons";
import { InvestmentService } from "@/services";
import parseHttpError from "@/utils/parseHttpError";
import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import React, { useEffect, useState } from "react";

interface User {
  id: string;
  full_name: string;
  email: string;
  role: string; // e.g., "regular-admin" or "user"
}

interface Document {
  id: string;
  type: string;
  upload_url: string;
}

interface DocumentsProps {
  investmentId: string;
  currentUser?: User; // logged-in user
}

const Documents: React.FC<DocumentsProps> = ({ investmentId, currentUser }) => {
  const [investmentDocuments, setInvestmentDocuments] = useState<Document[]>(
    [],
  );

  const getInvestmentDocuments = async () => {
    try {
      const response =
        await InvestmentService.GetInvestmentDocuments(investmentId);
      return response?.data; // Assuming API returns { data: Document[] }
    } catch (error: any) {
      console.error(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ["investmentDocuments", investmentId],
    queryFn: getInvestmentDocuments,
    staleTime: 0,
    enabled: !!investmentId,
  });

  useEffect(() => {
    if (data?.data) {
      setInvestmentDocuments(data.data);
    }
  }, [data]);

  if (isLoading) return <p>Loading documents...</p>;
  if (isError) return <p>Error loading documents.</p>;

  // Determine if user is an admin
  const isAdmin = currentUser?.role.toLowerCase().includes("admin");

  return (
    <div className="flex flex-col gap-8">
      {/* Always show Development Proposal documents as links */}
      {investmentDocuments
        .filter((doc) => doc.type === "Development Proposal")
        .map((doc, index) => (
          <Link href={doc.upload_url} key={index} target="_blank">
            <div className="border border-soft-200 w-[274px] h-[74px] p-4 flex items-center rounded-lg cursor-pointer">
              <div className="min-w-fit">
                <PaperClip />
              </div>
              <div className="flex flex-col gap-2 ml-2 w-[70%]">
                <p className="text-main-900">{doc.type.toUpperCase()}</p>
              </div>
            </div>
          </Link>
        ))}

      {/* List the rest of the documents with a check mark */}
      <ul className="flex flex-col gap-4">
        {investmentDocuments
          .filter((doc) => doc.type !== "Development Proposal")
          .map((doc, index) => (
            <li key={index} className="flex items-center gap-">
              <span className="text-main-900">{doc.type}</span>
              <span className="text-green-500 font-bold">✅</span>
            </li>
          ))}
      </ul>
    </div>
  );
};

export default Documents;
